import { useEffect, useMemo } from "react";
import { useElements } from "@stripe/react-stripe-js";
import { isEqual } from "lodash";
import { useFilterPaymentMethodsSupported } from "@/react/components/Paywalls/Checkout/hooks/useFilterPaymentMethodsSupported";
import { useStripeElementOptions } from "@/react/components/Paywalls/Checkout/hooks/useStripeElementOptions";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { stripeElementsOptions } from "../../StripeElements/stripeElementsOptions";
import { PaywallPriceOption } from "./PaywallPriceOption";

export const PaywallPricesWithoutPMC = () => {
  const {
    paywall,
    getSelectedPrice,
    setSelectedPrice,
    isSelectedPrice,
    currentCommunityMemberPaymentProcessorSessionClientSecret,
    isBuyNowPayLaterAllowed,
    setIsBuyNowPayLaterAllowed,
    isProcessingPayment,
    previewMutation,
    paymentMethodsSupported,
    disabledPaymentMethods,
  } = usePaywallCheckoutContext();
  const elements = useElements();
  const price = getSelectedPrice();
  const filteredPaymentMethodsSupported = useFilterPaymentMethodsSupported(
    paymentMethodsSupported,
    disabledPaymentMethods,
  );
  const otherStripeElementsOptions = useStripeElementOptions({
    filteredPaymentMethodsSupported,
    paywall,
    price,
    isBuyNowPayLaterAllowed,
    amountFromPreview: previewMutation.data?.amount_due_now,
    currentCommunityMemberPaymentProcessorSessionClientSecret,
  });
  const paymentElementOptions = useMemo(
    () => ({
      ...stripeElementsOptions,
      ...otherStripeElementsOptions,
    }),
    [
      paymentMethodsSupported.sort().join(","),
      disabledPaymentMethods.sort().join(","),
      price,
      isBuyNowPayLaterAllowed,
      previewMutation.data,
    ],
  );

  const onPriceChangeHandler = (priceId: any) => {
    setSelectedPrice(priceId);
    if (elements) {
      elements.update(paymentElementOptions);
    }
  };

  useEffect(() => {
    const isAmountDueZero = previewMutation.data
      ? isEqual(previewMutation.data.amount_due_now, 0)
      : false;
    if (isAmountDueZero && isBuyNowPayLaterAllowed) {
      setIsBuyNowPayLaterAllowed(false);
    } else if (
      !isAmountDueZero &&
      !isBuyNowPayLaterAllowed &&
      !isProcessingPayment
    ) {
      setIsBuyNowPayLaterAllowed(true);
    }

    if (!elements) return;
    elements.update(paymentElementOptions);
  }, [elements, paymentElementOptions]);

  return (
    <>
      {(paywall.prices || []).map((price: any) => (
        <PaywallPriceOption
          key={`paywall_price-${price.id}`}
          id={price.id}
          title={price.title}
          subtitle={price.subtitle}
          value={price.value}
          isSelected={isSelectedPrice(price)}
          onSelect={onPriceChangeHandler}
        />
      ))}
    </>
  );
};
